
import React from 'react';
import { UseFormRegister, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { MapPin, List, Tag, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GUIDE_TYPES } from '../constants/guideTypes';
import { BlogFormData } from '../types/blog';

interface BlogPostFormFieldsProps {
  register: UseFormRegister<BlogFormData>;
  watch: UseFormWatch<BlogFormData>;
  setValue: UseFormSetValue<BlogFormData>;
}

export const BlogPostFormFields = ({ register, watch, setValue }: BlogPostFormFieldsProps) => {
  const [tagInput, setTagInput] = React.useState('');
  const tags = watch('tags') || [];

  const handleAddTag = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && tagInput.trim()) {
      event.preventDefault();
      const newTag = tagInput.trim().toLowerCase();
      if (!tags.includes(newTag)) {
        setValue('tags', [...tags, newTag]);
      }
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setValue('tags', tags.filter((tag) => tag !== tagToRemove));
  };

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <label className="block">
          <span className="text-lg font-medium text-gray-700">Title</span>
          <Input
            placeholder="Give your travel story a captivating title..."
            {...register('title', { required: true })}
            className="mt-2 text-xl font-montserrat h-12"
          />
        </label>
      </div>
      
      <div className="grid gap-6">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="h-5 w-5 text-primary/70" />
            <span className="text-lg font-medium text-gray-700">City</span>
          </div>
          <Input
            placeholder="Which city is this guide about?"
            {...register('city', { required: true })}
            className="h-12"
          />
        </div>
        
        <div>
          <div className="flex items-center gap-2 mb-3">
            <List className="h-5 w-5 text-primary/70" />
            <span className="text-lg font-medium text-gray-700">Guide Type</span>
          </div>
          <Select
            onValueChange={(value) => setValue('guide_type', value)}
            defaultValue={watch('guide_type')}
          >
            <SelectTrigger className="h-12">
              <SelectValue placeholder="Select guide type" />
            </SelectTrigger>
            <SelectContent>
              {GUIDE_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <Tag className="h-5 w-5 text-primary/70" />
            <span className="text-lg font-medium text-gray-700">Tags</span>
          </div>
          <div className="space-y-3">
            <Input
              placeholder="Add tags (press Enter after each tag)..."
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleAddTag}
              className="h-12"
            />
            <div className="flex flex-wrap gap-2 min-h-[40px] p-2 bg-gray-50/50 rounded-lg">
              {tags.map((tag) => (
                <Badge 
                  key={tag} 
                  variant="secondary" 
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white"
                >
                  {tag}
                  <X 
                    className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors" 
                    onClick={() => removeTag(tag)}
                  />
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

